import React, {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmationModal} from '@pexip/components';
import type {Incoming} from '@pexip/infinity';
import {formatPath} from '@pexip/router';

import {TestId} from '../../test/testIds';
import {applicationConfig} from '../applicationConfig';
import {useIncomingCall, useRegistrationContext} from '../hooks/registration';
import {useCallHistoryContext} from '../hooks/useCallHistoryContext';
import {useNotificationContext} from '../hooks/useNotificationContext';
import {push} from '../router';
import {
    registrationIncomingCallSignal,
    registrationMissedCallSignal,
} from '../signals/Registration.signals';
import {toMissedCallRecent} from '../utils/toCallRecent';

import {PlaySound} from './PlaySound.viewModel';

const createMissedCallRecent = (call: Incoming) =>
    toMissedCallRecent({incomingCall: call, node: applicationConfig.node});

export const IncomingCall: React.FC = () => {
    const {t} = useTranslation();
    const registration = useRegistrationContext();
    const callHistory = useCallHistoryContext();
    const incoming = useIncomingCall();
    const notifications = useNotificationContext();

    const handleDismiss = useCallback(() => {
        if (incoming) {
            callHistory.addRecent(createMissedCallRecent(incoming));
        }
        void registration.declineIncomingCall();
    }, [incoming, registration, callHistory]);

    const handleJoin = useCallback(() => {
        if (incoming?.token) {
            push(formatPath`/m/${incoming.remote_alias}`, {
                state: {
                    token: incoming.token,
                },
            });
        }
        registration.dismissIncomingCall();
    }, [incoming, registration]);

    useEffect(
        () =>
            registrationMissedCallSignal.add(call => {
                callHistory.addRecent(createMissedCallRecent(call));
            }),
        [callHistory],
    );

    useEffect(
        () =>
            registrationIncomingCallSignal.add(call => {
                if (!call || document.visibilityState === 'visible') {
                    return;
                }

                notifications.sendNotification(call.remote_display_name, {
                    body: t(
                        'incoming-call.notification-body',
                        '{{uri}} is calling you with video...',
                        {uri: call.remote_alias},
                    ),
                    tag: 'incoming-call',
                });
            }),
        [t, notifications],
    );

    return (
        <>
            <PlaySound
                loop
                play={incoming !== undefined}
                controls={false}
                preload="auto"
                sound="incoming"
            />
            <ConfirmationModal
                isOpen={incoming !== undefined}
                onClose={handleDismiss}
                onCancel={handleDismiss}
                onConfirm={handleJoin}
                title={incoming?.remote_display_name}
                descriptionUrlLinks={false}
                description={
                    incoming
                        ? t(
                              'incoming-call.description',
                              '{{uri}} is calling you with video...',
                              {uri: incoming.remote_alias},
                          )
                        : undefined
                }
                cancelButtonText={t('incoming-call.decline', 'Decline')}
                cancelButtonTestid={TestId.ButtonIncomingCallDecline}
                confirmButtonText={t('incoming-call.answer', 'Answer')}
                confirmButtonTestid={TestId.ButtonIncomingCallAnswer}
                confirmButtonVariant="success"
                uniqueTitle={t('incoming-call.modal-title', 'Incoming call')}
                padding="medium"
                sizeModifier="compact"
                data-testid={TestId.ModalIncomingCall}
            />
        </>
    );
};
